import { useEffect, type RefObject } from 'react';

/** Whole gesture, first finger down to last finger up. */
const TAP_MS = 350;
/** All fingers of a tap land within this window (a resting palm arrives more slowly). */
const STAGGER_MS = 150;
/** A finger that travels further than this is a pan or pinch, not a tap. */
const MOVE_PX = 12;

function isTextTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  return t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable;
}

/**
 * Detects two- and three-finger taps on the viewport (undo / redo on touch screens).
 *
 * Works on raw touch events in the capture phase, so it sees every finger regardless of which
 * element captured its pointer (the draw canvas, an item, the viewport). A tap counts when
 * every finger lands within a short window of the first, none of them travels, all lift
 * within TAP_MS, and no pen is on the screen (fingers beside a working pen are a palm).
 */
export function useFingerTaps(ref: RefObject<HTMLElement | null>, onTap: (fingers: number) => void): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const starts = new Map<number, { x: number; y: number }>();
    let startAt = 0;
    let fingers = 0;
    let ok = false;
    let pens = 0;

    const reset = () => {
      starts.clear();
      fingers = 0;
      ok = false;
    };

    const onStart = (e: TouchEvent) => {
      const now = performance.now();
      // Every touch is new: a fresh gesture (also recovers if an earlier end never reached us).
      if (e.touches.length === e.changedTouches.length || !starts.size) {
        reset();
        startAt = now;
        const t = e.changedTouches[0]?.target as Element | null;
        ok = !!t?.closest?.('.viewport') && !isTextTarget(t) && !t.closest('.sel-box') && pens === 0;
      } else if (now - startAt > STAGGER_MS || pens > 0) {
        ok = false;
      }
      for (const t of Array.from(e.changedTouches)) starts.set(t.identifier, { x: t.clientX, y: t.clientY });
      fingers = Math.max(fingers, e.touches.length);
    };

    const onMove = (e: TouchEvent) => {
      if (!ok) return;
      for (const t of Array.from(e.changedTouches)) {
        const s = starts.get(t.identifier);
        if (s && Math.hypot(t.clientX - s.x, t.clientY - s.y) > MOVE_PX) {
          ok = false;
          return;
        }
      }
    };

    const onEnd = (e: TouchEvent) => {
      for (const t of Array.from(e.changedTouches)) starts.delete(t.identifier);
      if (e.type === 'touchcancel') ok = false;
      if (e.touches.length) return;
      const tap = ok && (fingers === 2 || fingers === 3) && performance.now() - startAt < TAP_MS;
      const n = fingers;
      reset();
      if (tap) onTap(n);
    };

    // A pen on the screen turns any finger into a palm.
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType === 'pen') pens++;
    };
    const onPointerEnd = (e: PointerEvent) => {
      if (e.pointerType === 'pen') pens = Math.max(0, pens - 1);
    };

    const opts: AddEventListenerOptions = { capture: true, passive: true };
    el.addEventListener('touchstart', onStart, opts);
    el.addEventListener('touchmove', onMove, opts);
    el.addEventListener('touchend', onEnd, opts);
    el.addEventListener('touchcancel', onEnd, opts);
    el.addEventListener('pointerdown', onPointerDown, opts);
    el.addEventListener('pointerup', onPointerEnd, opts);
    el.addEventListener('pointercancel', onPointerEnd, opts);
    return () => {
      el.removeEventListener('touchstart', onStart, opts);
      el.removeEventListener('touchmove', onMove, opts);
      el.removeEventListener('touchend', onEnd, opts);
      el.removeEventListener('touchcancel', onEnd, opts);
      el.removeEventListener('pointerdown', onPointerDown, opts);
      el.removeEventListener('pointerup', onPointerEnd, opts);
      el.removeEventListener('pointercancel', onPointerEnd, opts);
    };
  }, [ref, onTap]);
}
